import { useCallback, useMemo } from 'react'
import { useCacheSlice, useCacheMutations } from '../hooks'
import { useApi } from '@/shared/utils/request'

export type CachedAbbreviation = {
  id: number
  abbreviation: string
  expansion: string
}

/** Shape stored in the cache slice — mirrors GET /abbreviations response */
export type AbbreviationsSliceData = {
  enabled: boolean
  abbreviations: CachedAbbreviation[]
}

export function useAbbreviationsCache() {
  const { data, isLoading } = useCacheSlice('abbreviations')
  const { optimisticUpdate, refresh } = useCacheMutations()
  const { makeApiRequest } = useApi()

  const { enabled, abbreviations = [] } = (data ?? { enabled: true, abbreviations: [] }) as AbbreviationsSliceData

  // Derived: lookup map for textarea expansion
  const abbreviationMap = useMemo(
    () => new Map((abbreviations ?? []).map((a) => [a.abbreviation, a.expansion])),
    [abbreviations],
  )

  // Derived: prop format for NarrativeTextArea, gated on enabled flag
  const abbreviationProps = useMemo(
    () =>
      enabled
        ? (abbreviations ?? []).map((a) => ({ trigger: a.abbreviation, replacement: a.expansion }))
        : [],
    [abbreviations, enabled],
  )

  /** Optimistically flip the local `enabled` flag (does NOT persist — caller must save via settings).
   *  Returns a rollback function the caller should invoke if the settings update fails. */
  const setLocalEnabled = useCallback(
    (value: boolean) => {
      return optimisticUpdate('abbreviations', (prev: AbbreviationsSliceData) => ({
        ...prev,
        enabled: value,
      }))
    },
    [optimisticUpdate],
  )

  const createAbbreviation = useCallback(
    async (abbreviation: string, expansion: string) => {
      const tempId = -Date.now()
      const rollback = optimisticUpdate('abbreviations', (prev: AbbreviationsSliceData) => ({
        ...prev,
        abbreviations: [
          ...prev.abbreviations,
          { id: tempId, abbreviation, expansion },
        ],
      }))

      try {
        const res = await makeApiRequest({
          endpoint: '/abbreviations',
          method: 'POST',
          body: { abbreviation, expansion },
        })
        if (res.error) throw new Error(String(res.error))
        // Replace temp ID with real one
        optimisticUpdate('abbreviations', (prev: AbbreviationsSliceData) => ({
          ...prev,
          abbreviations: prev.abbreviations.map((a) =>
            a.id === tempId ? { ...a, id: res.data.id } : a,
          ),
        }))
        return { ok: true }
      } catch (e) {
        rollback()
        return { ok: false, error: e instanceof Error ? e.message : 'Failed to create' }
      }
    },
    [optimisticUpdate, makeApiRequest],
  )

  const updateAbbreviation = useCallback(
    async (id: number, fields: { abbreviation?: string; expansion?: string }) => {
      const rollback = optimisticUpdate('abbreviations', (prev: AbbreviationsSliceData) => ({
        ...prev,
        abbreviations: prev.abbreviations.map((a) =>
          a.id === id ? { ...a, ...fields } : a,
        ),
      }))

      try {
        const res = await makeApiRequest({
          endpoint: `/abbreviations/${id}`,
          method: 'PUT',
          body: fields,
        })
        if (res.error) throw new Error(String(res.error))
        return { ok: true }
      } catch (e) {
        rollback()
        return { ok: false, error: e instanceof Error ? e.message : 'Failed to update' }
      }
    },
    [optimisticUpdate, makeApiRequest],
  )

  const deleteAbbreviation = useCallback(
    async (id: number) => {
      const rollback = optimisticUpdate('abbreviations', (prev: AbbreviationsSliceData) => ({
        ...prev,
        abbreviations: prev.abbreviations.filter((a) => a.id !== id),
      }))

      try {
        const res = await makeApiRequest({
          endpoint: `/abbreviations/${id}`,
          method: 'DELETE',
        })
        if (res.error) throw new Error(String(res.error))
        return { ok: true }
      } catch (e) {
        rollback()
        return { ok: false, error: e instanceof Error ? e.message : 'Failed to delete' }
      }
    },
    [optimisticUpdate, makeApiRequest],
  )

  return {
    enabled,
    abbreviations,
    abbreviationMap,
    abbreviationProps,
    isLoading,
    setLocalEnabled,
    createAbbreviation,
    updateAbbreviation,
    deleteAbbreviation,
    refresh: () => refresh('abbreviations'),
  }
}
